import React, { useRef, useCallback, useEffect } from 'react'
import { Tooltip, IconButton } from '@mui/material'
import {
    Send as SendIcon,
    Stop as StopIcon,
    Image as ImageIcon,
} from '@mui/icons-material'
import { VoiceButton } from './VoiceButton'
import { ImagePreviewRow } from './ImagePreviewRow'
import {
    InputContainer,
    InputWrapper,
    ChatInput,
    SendButton,
    ComposerActions,
} from '../styles'
import {
    type AttachedImage,
    processImageFiles,
    MAX_ATTACHED_IMAGES,
} from '../utils/imageAttachment'

interface ChatComposerProps {
    value: string
    onChange: (value: string) => void
    onSend: () => void
    onStop: () => void
    isLoading: boolean
    disabled: boolean
    canSend: boolean
    placeholder: string
    enableVoice: boolean
    transcribeUrl: string
    allowImages: boolean
    attachedImages: AttachedImage[]
    onAttachedImagesChange: (images: AttachedImage[]) => void
}

export const ChatComposer: React.FC<ChatComposerProps> = ({
    value,
    onChange,
    onSend,
    onStop,
    isLoading,
    disabled,
    canSend,
    placeholder,
    enableVoice,
    transcribeUrl,
    allowImages,
    attachedImages,
    onAttachedImagesChange,
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const adjustHeight = useCallback(() => {
        const el = textareaRef.current
        if (!el) return
        el.style.height = 'auto'
        el.style.height = `${Math.min(el.scrollHeight, 120)}px`
    }, [])

    useEffect(() => {
        adjustHeight()
    }, [value, adjustHeight])

    const handleVoiceTranscript = useCallback(
        (text: string) => {
            const trimmed = text.trim()
            if (!trimmed) return
            onChange(value.trim() ? `${value.trim()} ${trimmed}` : trimmed)
            requestAnimationFrame(adjustHeight)
        },
        [value, onChange, adjustHeight],
    )

    const handlePaste = useCallback(
        async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
            if (!allowImages) return

            const items = e.clipboardData?.items
            if (!items) return

            const imageFiles: File[] = []
            for (let i = 0; i < items.length; i++) {
                const item = items[i]
                if (item.type.startsWith('image/')) {
                    const file = item.getAsFile()
                    if (file) imageFiles.push(file)
                }
            }

            if (imageFiles.length === 0) return
            e.preventDefault()
            const next = await processImageFiles(imageFiles, attachedImages)
            onAttachedImagesChange(next)
        },
        [allowImages, attachedImages, onAttachedImagesChange],
    )

    const handleFilePick = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files ?? [])
            e.target.value = ''
            if (files.length === 0) return
            const next = await processImageFiles(files, attachedImages)
            onAttachedImagesChange(next)
        },
        [attachedImages, onAttachedImagesChange],
    )

    const handleRemoveImage = useCallback(
        (id: string) => {
            onAttachedImagesChange(attachedImages.filter((img) => img.id !== id))
        },
        [attachedImages, onAttachedImagesChange],
    )

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (isLoading) onStop()
                else if (canSend) onSend()
            }
        },
        [isLoading, canSend, onSend, onStop],
    )

    const validImageCount = attachedImages.filter((img) => !img.error).length
    const imagesFull = validImageCount >= MAX_ATTACHED_IMAGES

    return (
        <InputContainer>
            {allowImages && (
                <ImagePreviewRow images={attachedImages} onRemove={handleRemoveImage} />
            )}
            <InputWrapper>
                <ComposerActions>
                    {enableVoice && (
                        <VoiceButton
                            transcribeUrl={transcribeUrl}
                            onTranscript={handleVoiceTranscript}
                            disabled={disabled || isLoading}
                        />
                    )}
                    {allowImages && (
                        <>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                                multiple
                                hidden
                                onChange={handleFilePick}
                            />
                            <Tooltip
                                title={
                                    imagesFull
                                        ? `Maximum ${MAX_ATTACHED_IMAGES} images`
                                        : 'Attach image'
                                }
                                placement="top"
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label="Attach image"
                                        disabled={disabled || isLoading || imagesFull}
                                        onClick={() => fileInputRef.current?.click()}
                                        sx={{
                                            color: 'var(--main-text-secondary, #64748b)',
                                        }}
                                    >
                                        <ImageIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </>
                    )}
                </ComposerActions>
                <ChatInput
                    ref={textareaRef}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    placeholder={placeholder}
                    disabled={disabled}
                    rows={1}
                    aria-label="Chat message input"
                />
                <Tooltip title={isLoading ? 'Stop' : 'Send'} placement="top">
                    <span>
                        <SendButton
                            onClick={isLoading ? onStop : onSend}
                            disabled={disabled || (!isLoading && !canSend)}
                            isLoading={isLoading}
                            aria-label={isLoading ? 'Stop response' : 'Send message'}
                        >
                            {isLoading ? (
                                <StopIcon sx={{ fontSize: 20 }} />
                            ) : (
                                <SendIcon sx={{ fontSize: 20 }} />
                            )}
                        </SendButton>
                    </span>
                </Tooltip>
            </InputWrapper>
        </InputContainer>
    )
}

export default ChatComposer
